import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import LoadingScreen from "../../progress/loading-screen";
import { IngestionKind } from "../types";

const LABELS: Record<IngestionKind, string> = {
  sftp: "SFTP",
  email: "email",
};

const PAGE_SIZE = 10;

export interface IngestionListProps<T extends GridValidRowModel> {
  kind: IngestionKind;
  rows: T[] | undefined;
  isLoading: boolean;
  // SWR returns any, so a more explicit type cannot be used
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  columns: GridColDef<T>[];
  onAdd: () => void;
}

const IngestionList = <T extends GridValidRowModel>({
  kind,
  rows,
  isLoading,
  error,
  columns,
  onAdd,
}: IngestionListProps<T>) => {
  const label = LABELS[kind];

  const renderContent = () => {
    if (error) {
      return (
        <Alert severity="error">{`Error loading ${label} ingestions.`}</Alert>
      );
    }
    if (isLoading || rows === undefined) {
      return <LoadingScreen dataTestId="ingestion-list-loading" />;
    }
    // Not every client ingests through both SFTP and email, so an empty list
    // is a normal state rather than a problem.
    if (rows.length === 0) {
      return (
        <Alert severity="info">{`No ${label} ingestion is configured for this client.`}</Alert>
      );
    }
    return (
      <DataGrid
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        initialState={{
          pagination: { paginationModel: { pageSize: PAGE_SIZE } },
        }}
        pageSizeOptions={[PAGE_SIZE, 25, 50]}
      />
    );
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
          {`Add ${label} ingestion`}
        </Button>
      </Stack>
      {renderContent()}
    </Box>
  );
};

export default IngestionList;
